import { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Context Providers
import { AuthProvider, useAuth, ProtectedRoute } from './contexts/AuthContext';
import { DarkModeProvider } from './contexts/DarkModeContext';

// Layout Components
import PublicSiteLayout from './components/layout/PublicSiteLayout';
import AdminLayout from './components/layout/AdminLayout';

// Public Page Components
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import ServicesPage from './pages/public/ServicesPage';
import ProjectsPage from './pages/public/ProjectsPage';
import ProjectImageDetailPage from './pages/public/ProjectImageDetailPage';
import ContactPage from './pages/public/ContactPage';

// Admin Page Components
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ImageManagementPage from './pages/admin/ImageManagementPage';
import InquiryManagementPage from './pages/admin/InquiryManagementPage';

// ScrollToTop 컴포넌트: 페이지 이동 시 최상단으로 스크롤
const ScrollToTopOnRouteChange = () => {
  const location = useLocation();
  useEffect(() => {
    // 이미지 상세 페이지 내에서의 네비게이션이 아닐 경우에만 최상단으로 스크롤
    if (!location.state?.keepScroll) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.state?.keepScroll]); 

  return null; 
};

// 메인 라우팅 로직을 담는 컴포넌트
const MainRouter = () => {
  const { logout } = useAuth(); 

  const handleAdminLogout = () => {
    logout(); 
  };

  return (
    <>
      <ScrollToTopOnRouteChange /> 
      <Routes>
        {/* 관리자 페이지 라우트 */}
        <Route path="/jy-dev/login" element={<AdminLoginPage />} /> {/* onLogin prop은 AuthContext에서 처리 */}
        <Route 
          path="/jy-dev" 
          element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout} /> 
            </ProtectedRoute>
          } 
        >
          <Route index element={<Navigate to="dashboard" replace />} /> 
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="images" element={<ImageManagementPage />} />
          <Route path="inquiries" element={<InquiryManagementPage />} />
        </Route>

        {/* 일반 사용자 사이트 라우트 */}
        <Route path="/" element={<PublicSiteLayout />}> 
          <Route index element={<HomePage />} /> 
          <Route path="about" element={<AboutPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/gallery/:imageId" element={<ProjectImageDetailPage />} /> 
          <Route path="contact" element={<ContactPage />} />
          <Route path="privacy" element={
            <>
              <title>개인정보처리방침 - 진양건설</title>
              <div className="container mx-auto p-8 text-center pt-24 min-h-[50vh]">개인정보처리방침 페이지입니다. (내용 추가 필요)</div>
            </>
          } />
          <Route path="terms" element={
            <>
              <title>이용약관 - 진양건설</title>
              <div className="container mx-auto p-8 text-center pt-24 min-h-[50vh]">이용약관 페이지입니다. (내용 추가 필요)</div>
            </>
          } />
          <Route path="support" element={
            <>
              <title>고객지원 - 진양건설</title>
              <div className="container mx-auto p-8 text-center pt-24 min-h-[50vh]">고객지원 페이지입니다. (내용 추가 필요)</div>
            </>
          } />
          <Route path="*" element={<Navigate to="/" replace />} /> 
        </Route>
      </Routes>
    </>
  );
}

// 최상위 App 컴포넌트
const App = () => {
  return (
    <AuthProvider> 
      <DarkModeProvider> 
        <Router> 
          <MainRouter />
        </Router>
      </DarkModeProvider>
    </AuthProvider>
  );
};

export default App;
